import React, { FC } from 'react';

import AvailableLanguageLink from '../AvailableLanguageLink';
import { Link } from 'src/components/Inputs';

interface AvailableLanguage {
  href: string;
  label: string;
}

interface Props {
  availableLanguages: AvailableLanguage[];
}

const sections = [
  { title: 'Aperçu' },
  { title: 'Données que nous pouvons recueillir' },
  { title: 'Informations biométriques et diffusion' },
  {
    title:
      'Comment nous pouvons utiliser et divulguer vos Données à caractère personnel pour fournir des Services IDV aux Clients',
  },
  {
    title:
      'Comment nous pouvons utiliser des données anonymisées ou regroupées pour maintenir, améliorer et développer les Services IDV',
  },
  { title: 'Vos droits et choix' },
  { title: 'Sécurité et conservation' },
  {
    title: 'Mises à jour de la présente Déclaration de confidentialité IDV',
  },
  { title: 'Contacter Plaid' },
  {
    title:
      "Pour les personnes résidant dans l'Union européenne et au Royaume-Uni",
  },
];

const FrVersion: FC<Props> = ({ availableLanguages }) => {
  return (
    <>
      <h4 id='declaration-de-confidentialite-idv'>
        Déclaration de confidentialité IDV
      </h4>

      <p>Langues disponibles:</p>
      <ul>
        {availableLanguages.map((language, i) => {
          return (
            <AvailableLanguageLink
              key={`language-${i}`}
              href={language.href}
              label={language.label}
            />
          );
        })}
      </ul>
      <br />
      <br />

      <p>Dernière mise à jour le : 20 février 2026</p>

      <p>
        Plaid Inc. et ses filiales, Plaid Financial Ltd. et Plaid, B.V.
        (collectivement, « Plaid », « nous », « notre » ou « nos ») proposent
        des services de vérification d'identité, de détection des fraudes et de
        filtrage des listes de surveillance (les « Services IDV » ou « Services
        ») à l'échelle mondiale aux sociétés, sociétés et institutions
        financières (« Clients » ou « Client »).
      </p>

      <p>
        L'un de nos Clients peut vous demander d'effectuer diverses
        vérifications en utilisant les Services IDV. La présente Déclaration de
        confidentialité IDV (la « Déclaration de confidentialité IDV ») explique
        comment vos données à caractère personnel (les « Données à caractère
        personnel ») peuvent être utilisées lorsque nous exécutons les Services
        IDV pour le compte de nos Clients.
      </p>

      <p>
        Plaid ne recueille, ne traite, n'utilise ou ne partage pas sciemment des
        Données à caractère personnel pour les consommateurs de moins de 18 ans.
        Les Services IDV sont destinés uniquement aux personnes âgées de 18 ans
        ou plus. Si vous avez moins de 18 ans, veuillez ne pas utiliser les
        Services IDV.
      </p>

      <p>
        Lorsque nous mettons des Services IDV à disposition de nos Clients, nos
        Clients déterminent l'objectif et les moyens par lesquels des Données à
        caractère personnel sont traitées (et nos Clients sont considérés comme
        un responsable du traitement). Nous agissons selon les instructions de
        nos Clients en ce qui concerne la manière et la raison du traitement de
        vos Données à caractère personnel. Plaid est un sous-traitant de données
        ou un sous-traitant ultérieur lorsqu'il facilite la vérification de
        l'identité, la détection de la fraude et les services de contrôle de
        liste de surveillance sur instruction de nos Clients. Nos Clients nous
        demandent de recueillir vos Données à caractère personnel. Plaid agit en
        tant que sous-traitant ou sous-traitant ultérieur lorsqu'il reçoit des
        instructions pour recueillir des données auprès de vous, y compris des
        Données à caractère personnel, et Plaid reçoit des instructions de la
        part de nos Clients concernant les personnes auprès desquelles
        recueillir des Données à caractère personnel et quand recueillir et
        traiter des Données à caractère personnel. Nous utilisons vos Données à
        caractère personnel pour fournir des Services IDV à nos Clients. Nous
        transférons et/ou donnons également accès au téléchargement des
        informations que nous recueillons auprès de vous, y compris des Données
        à caractère personnel, aux Clients à leur demande et à leurs
        instructions.
      </p>

      <p>
        Vous devez toujours vérifier les politiques de confidentialité de nos
        Clients et leur adresser toute question et demande d'exercice de vos
        droits concernant vos Données à caractère personnel.
      </p>

      <p>
        Vous pouvez accéder directement à certains Services IDV, y compris par
        l'intermédiaire de nos sites Web qui font référence à la présente
        politique (collectivement, les « Sites »). Dans certains cas, par
        exemple, si vous êtes un Client ou si vous souhaitez faire une demande
        de renseignements concernant les Services IDV, ou si vous visitez notre
        site Web, nous pouvons être un responsable du traitement des données.
        Nous décrivons comment et pourquoi nous utilisons les Données à
        caractère personnel dans ces circonstances dans les politiques de
        confidentialité applicables de Plaid à l'adresse{' '}
        <Link href='https://plaid.com/legal/'>
          <a>www.plaid.com/legal/</a>
        </Link>
        .
      </p>

      <p>
        Plaid ne vendra jamais vos Données à caractère personnel des Services
        IDV à des tiers. Nos Clients décident de la manière dont nous utilisons
        vos Données à caractère personnel, contrôlent leurs politiques de
        conservation des données et peuvent préciser combien de temps conserver
        des Données à caractère personnel et si et quand nous partageons les
        informations que nous recueillons, y compris vos Données à caractère
        personnel, avec notre Client.
      </p>

      <p>
        En fonction du service requis par nos Clients, nous pouvons traiter des
        Données à caractère personnel pour le compte de nos Clients comme décrit
        dans la présente Déclaration de confidentialité IDV, nous vous
        encourageons donc à les lire attentivement.
      </p>

      <nav>
        <h5>Aller à la section:</h5>
        <ol>
          {sections.map((section, index) => {
            return (
              <li key={index}>
                <a href={`#section-fr-${index + 1}`}>{`${section.title}`}</a>
              </li>
            );
          })}
        </ol>
      </nav>

      <div id='section-fr-1'>
        <h5>1. Aperçu</h5>
        <p>
          Lorsque Plaid fournit des Services IDV aux Clients, il vous sera
          peut-être demandé de nous confier des informations vous concernant. Au
          nom de nos Clients, nous effectuons des services de vérification
          d'identité, de détection de la fraude et de vérification des listes de
          surveillance basés sur le cloud. Les Services IDV recueillent des
          données auprès de vous et à votre sujet pour prévoir et contribuer à
          prévenir les activités frauduleuses en temps réel. Plaid effectue
          ensuite nos Services IDV et en confie les résultats directement à nos
          Clients. À la demande de notre Client, nous transférons ou produisons
          une copie des informations que vous partagez, y compris vos Données à
          caractère personnel, pour l'utilisation du Client et dans le cadre des
          Services IDV. Nous pouvons également transférer et/ou donner aux
          Clients un accès leur permettant de télécharger les informations que
          nous recueillons auprès de vous, y compris vos Données à caractère
          personnel, sur instructions de notre Client.
        </p>
        <p>
          Nos clients déterminent la portée de la demande d'informations et les
          Données à caractère personnel vous concernant qui pourraient être
          utilisées pour l'analyse avec les Services IDV. Les Clients
          configurent les Services IDV pour qu'ils nécessitent et utilisent
          certaines informations afin de vérifier votre identité. Un Client peut
          configurer les Services IDV pour demander des informations limitées.
          Si les informations demandées sont insuffisantes pour produire une
          vérification, les Services IDV peuvent, sur instruction de notre
          Client, demander des informations supplémentaires pour terminer le
          processus de vérification.
        </p>
        <p>
          Si vous n'êtes pas satisfait(e) du résultat d'un traitement, vous
          devriez adresser votre requête au Client.
        </p>
      </div>

      <div id='section-fr-2'>
        <h5>2. Données que nous pouvons recueillir</h5>
        <p>
          En fonction des Services IDV demandés par nos Clients, nous pouvons
          collecter vos Données à caractère personnel des manières suivantes:
        </p>
        <p>
          <strong>Données que vous nous confiez:</strong> Nous recueillons vos
          Données à caractère personnel lorsque vous nous confiez les
          informations demandées par le Client. Si vous choisissez de ne pas
          confier les informations demandées, nous ne serons pas en mesure de
          fournir les Services IDV à notre Client. Veuillez contacter le Client
          pour discuter des autres options de vérification de l'identité et de
          contrôle, si elles sont disponibles pour votre région ou nationalité.
        </p>
        <p>
          Certaines des informations potentielles vous concernant qui peuvent
          être demandées au nom de nos Clients comprennent vos coordonnées,
          notamment votre nom, adresse e-mail, adresse postale, numéro de
          téléphone, date de naissance, documents d'identité avec votre image,
          numéro d'identité attribué par le gouvernement, tel qu'un numéro de
          sécurité sociale, et/ou une photographie ou une image vidéo de vous,
          afin d'évaluer si une personne correspond à son document d'identité et
          semble être une vraie personne.
        </p>
        <p>
          Nous recueillons des données directement auprès de vous en recourant à
          des techniques standard telles que le code JavaScript, les SDK, les
          envois de formulaires, la collecte d'images, la collecte de vidéos et
          les comportements à l'aide d'outils que nos clients intègrent sur
          leurs sites Web ou applications mobiles («{' '}
          <strong>Sites clients</strong> »).
        </p>
        <p>
          <strong>
            Informations utilisées pour vérifier un numéro de téléphone
          </strong>
          : Nous pouvons également proposer une fonction d'authentification à
          deux facteurs à nos Clients pour aider à fournir des mesures afin de
          garantir que la personne fournissant les informations possède le
          numéro de téléphone associé à cette identité. Pour fournir cette
          fonctionnalité à nos Clients, nous pouvons utiliser vos Données à
          caractère personnel fournies par notre Client ou par vous pour vous
          envoyer des codes de vérification, par exemple par SMS, que vous
          pouvez ensuite saisir pour confirmer votre identité lorsque vous
          utilisez un Site client ou créez un nouveau compte avec un Client.
        </p>
        <p>
          <strong>
            Les données que nous recueillons automatiquement lorsque vous
            consultez les Sites clients:
          </strong>{' '}
          Les Services IDV utilisent certaines technologies de suivi standard
          pour recueillir automatiquement certaines informations sur votre
          appareil lorsque vous interagissez avec les Sites clients et les
          utilisez, y compris l'analyse comportementale de votre interaction
          avec les Services IDV ainsi que la fréquence à laquelle un appareil
          est utilisé pour interagir avec les Sites clients et les Services IDV.
          Certaines de ces informations comprennent, par exemple, votre adresse
          IP et certains identifiants uniques, et peuvent identifier un
          ordinateur ou un appareil particulier et peuvent être considérées
          comme des Données à caractère personnel dans certaines juridictions, y
          compris l'UE.
        </p>
        <p>
          Les types d'informations que nous pouvons recueillir pour le compte de
          nos Clients dépendront de votre visite d'un Site client via une
          interface API, une application utilisant notre SDK ou une page Web,
          notamment:
        </p>
        <ul>
          <li>
            <strong>Données concernant le navigateur et l'appareil</strong>, y
            compris le type et le modèle de l'appareil, le fabricant, le type et
            la version du système d'exploitation, le type et la version du
            navigateur Web, l'agent utilisateur, le nom et les informations de
            l'opérateur téléphonique, le fuseau horaire, le type de connexion
            réseau, l'adresse IP, les identifiants matériels, le nom de l'hôte,
            les identifiants de l'appareil, l'identifiant Android/Google
            Advertising ID, l'empreinte digitale en toile, les caractéristiques
            liées à l'émulation ou à l'enracinement, par exemple si votre
            appareil est « débridé », ainsi que le nom et la version de
            l'application. Nous recueillons également l'ensemble de caractères,
            le nom d'hôte, la langue, le titre et l'URL de la page, l'URL du
            référent, le nombre de polices, le hachage des polices, le nombre de
            modules, le hachage des modules, la hauteur et la largeur de
            l'écran, la profondeur de couleur, la plateforme, l'empreinte des
            cookies, le nombre maximum de points de contact, l'activation de
            Java, le stockage de session, le stockage local, l'éventuelle
            altération de la résolution, la langue ou le système d'exploitation,
            l'éventuelle activation du blocage des publicités, l'éventuelle
            activation du refus du suivi (do not track), l'IP de la prise flash
            et l'identifiant flash. Le SDK recueillera également les métadonnées
            liées au téléphone telles que le niveau de batterie, les propriétés
            de l'appareil, ainsi que le nom de l'opérateur.
          </li>
          <li>
            <strong>
              Informations concernant votre comportement sur les Sites clients
            </strong>
            , c.-à-d. données sur vos activités sur les Sites client, ID de
            session, heure de début et de fin de session, décalage de fuseau
            horaire et informations de localisation qui peuvent être des
            informations générales de localisation déduites de votre adresse IP
            ou, dans certaines circonstances, des informations de localisation
            plus précises basées sur les coordonnées de latitude et de
            longitude. Vous pouvez contrôler la collecte d'informations de
            localisation par l'intermédiaire de Sites clients particuliers en
            modifiant les préférences sur votre appareil mobile.
          </li>
        </ul>
        <p>
          <strong>
            Comment nous utilisons les technologies de suivi pour recueillir des
            données vous concernant.
          </strong>{' '}
          Nous utilisons des technologies de suivi standard pour recueillir
          automatiquement certaines informations à partir de votre appareil et
          de votre navigateur lorsque vous consultez les Sites clients ou
          interagissez avec eux. Lorsque vous consultez ou utilisez les Services
          IDV via un Site client, les serveurs Plaid sont informés et nous
          sommes en mesure de recueillir des informations à partir du navigateur
          ou de l'application. En outre, nous recueillons des données
          directement auprès de vous par l'intermédiaire de technologies de
          suivi standard telles que JavaScript ou SDK, que nos Clients peuvent
          intégrer sur leurs sites Web.
        </p>
        <p>
          Les types de technologies de suivi que nous pouvons utiliser
          comprennent:
        </p>
        <ul>
          <li>
            <strong>Le code JavaScript (JS Snippet)</strong>, également appelé
            balise ou pixel, est un petit snippet de code inséré dans le contenu
            du Site client. Vous pouvez désactiver JavaScript en modifiant les
            paramètres de votre navigateur. Des informations sur la façon de
            modifier ces paramètres peuvent généralement être trouvées sur le
            site Web du fournisseur de votre navigateur Internet. Étant donné
            que Plaid ne contrôle pas ces paramètres, nous vous encourageons à
            vérifier régulièrement les informations fournies pour vous assurer
            que vous êtes au courant de tout changement pertinent.
          </li>
          <li>
            <strong>SDK mobiles (kits de développement logiciel)</strong>, ou
            blocs de code intégrés dans un site client qui permettent à Plaid de
            recueillir certaines informations. Vous pouvez contrôler
            l'utilisation de certaines données recueillies par Plaid via le SDK
            en suivant les instructions applicables au système d'exploitation de
            votre appareil mobile. Vous pouvez également contrôler si et comment
            votre appareil mobile fournit des données de localisation sur des
            Sites clients particuliers en modifiant les préférences sur votre
            appareil mobile. Étant donné que Plaid ne contrôle pas ces
            paramètres, nous vous encourageons à vérifier régulièrement les
            informations fournies pour vous assurer que vous êtes au courant de
            tout changement pertinent.
          </li>
          <li>
            <strong>Canvas Fingerprinting</strong>, une technique de suivi qui
            nous permet de rendre des images graphiques à partir des
            fonctionnalités intégrées de HTML5 Canvas dans votre navigateur.
            L'image toile est souvent rendue différemment sur différents
            appareils en raison d'un certain nombre de facteurs tels que la
            version de votre navigateur Web, le système d'exploitation et ses
            paramètres, et le matériel graphique installé, et nous permet de
            vous distinguer des autres utilisateurs. Si vous souhaitez
            désactiver l'empreinte en toile, vous pouvez choisir de désactiver
            JavaScript ou de télécharger une extension de navigateur qui bloque
            les techniques d'empreinte digitale en toile, mais cela peut altérer
            la fonctionnalité des Services IDV. Étant donné que Plaid ne
            contrôle pas ces paramètres ou extensions, nous vous encourageons à
            vérifier régulièrement les informations fournies à leur sujet pour
            vous assurer que vous êtes au courant de tout changement pertinent.
          </li>
          <li>
            <strong>Device Fingerprinting</strong>, une technique de suivi qui
            nous permet de vous identifier à l'aide des informations relatives
            au logiciel et au matériel de votre appareil, y compris les
            composants de votre empreinte digitale en toile combinés aux
            informations de votre appareil. Plaid utilise l'empreinte digitale
            des appareils pour arrêter la fraude et d'autres acteurs
            malveillants, ainsi que pour la cybersécurité.
          </li>
        </ul>
        <p>
          Certaines de ces informations, telles que votre adresse IP et certains
          identifiants uniques, peuvent identifier un ordinateur ou un appareil
          particulier et peuvent donc être considérées comme des « Données à
          caractère personnel » dans certaines juridictions, y compris l'UE et
          la Californie.
        </p>
        <p>
          <strong>
            Données que nous recueillons auprès de prestataires de services et
            de sources tierces:
          </strong>{' '}
          À la demande de notre Client et dans le cadre des Services IDV pour
          les Clients, nous pouvons combiner les données que nous recueillons à
          votre sujet avec celles que nous recevons de prestataires de services.
          Par exemple, nous pouvons recevoir des données qui indiquent si une
          adresse IP est commerciale ou privée, si un numéro de téléphone est
          fixe ou si un domaine de messagerie est gratuit. Les Clients peuvent
          également nous demander de travailler avec des tiers qui font
          correspondre les données confiées avec des sources tierces telles que
          les fichiers d'en-tête de crédit, les dossiers de compte mobile, les
          dossiers de services publics, les dossiers de véhicules à moteur et
          d'autres sources réputées. Pour certains pays, votre opérateur mobile
          peut être invité à divulguer les détails de votre compte mobile afin
          de vérifier votre identité, y compris votre nom, votre adresse et les
          détails de votre appareil. Nous pouvons également recevoir des données
          vous concernant de la part de tiers et/ou recueillir des données vous
          concernant qui sont accessibles au public, pour le compte de notre
          Client.
        </p>
        <p>
          <strong>
            Catégories particulières de données à caractère personnel.
          </strong>{' '}
          Si nos Clients vous demandent de nous confier un document contenant
          votre photographie ou si vous devez prouver votre identité en
          produisant une photographie ou une vidéo de vous-même, ces images
          peuvent révéler des catégories particulières de données vous
          concernant, par exemple des informations relatives à votre santé
          (notamment si vous portez des lunettes), votre couleur de peau ou
          votre origine ethnique, ou des données relatives à vos croyances
          religieuses ou politiques. Nous utilisons ces catégories spéciales de
          données à caractère personnel uniquement sur instruction de notre
          Client et conformément aux politiques de confidentialité de nos
          Clients et à la présente Déclaration de confidentialité IDV, à
          l'exclusion de tout autre motif. Plaid IDV ne vend pas de Données à
          caractère personnel.
        </p>
      </div>

      <div id='section-fr-3'>
        <h5>3. Informations biométriques et diffusion</h5>
        <p>
          Si notre Client vous demande de nous fournir un document d'identité
          contenant une image de votre visage et une photographie ou une image
          vidéo (parfois appelée selfie), nous pouvons comparer votre géométrie
          faciale dérivée de l'image de votre document d'identité à votre
          géométrie faciale dérivée dans la photographie ou l'image vidéo que
          vous fournissez pour déterminer si une personne correspond à son
          document d'identité. Nous pouvons également utiliser votre scan de
          géométrie faciale pour détecter et prévenir la fraude, y compris pour
          déterminer si l'image vidéo est d'une vraie personne et/ou pour
          déterminer votre âge estimé, le cas échéant. L'image, la photographie
          ou l'image vidéo de votre document d'identité, ainsi que toute
          géométrie faciale dérivée de ces images, peuvent être considérées
          comme des informations biométriques dans certaines juridictions.
        </p>
        <p>
          En utilisant les Services IDV pour vous connecter à une application ou
          à un service fourni par notre Client, vous acceptez d'avoir lu,
          compris et consenti volontairement à{' '}
          <Link href='#biometric-policy-and-release'>
            <a>la Politique biométrique et à la Mainlevée de Plaid</a>
          </Link>
          , et de libérer en faveur de Plaid et des prestataires de services de
          Plaid toute réclamation liée à la collecte, au traitement, à
          l'utilisation, à la divulgation ou au partage de vos images,
          photographies ou images vidéo, y compris toute donnée dérivée de ces
          images pour créer des numérisations de géométrie faciale.
        </p>
        <p>
          Pour plus d'informations sur la collecte, l'utilisation, le
          traitement, la divulgation et la conservation de votre image à partir
          de votre document d'identité, photographie ou image vidéo, y compris
          toute numérisation de géométrie faciale dérivée à partir de ces images
          qui peuvent être considérées comme des données biométriques dans votre
          région, veuillez consulter{' '}
          <Link href='#biometric-policy-and-release'>
            <a>la Politique biométrique et la Mainlevée de Plaid</a>
          </Link>
          .
        </p>
      </div>

      <div id='section-fr-4'>
        <h5>
          4. Comment nous pouvons utiliser et divulguer vos Données à caractère
          personnel pour fournir des Services IDV aux Clients
        </h5>
        <p>
          Nos Clients peuvent utiliser nos Services IDV et nous renvoyons des
          informations au Client sur la probabilité que la personne qui produit
          les données soit bien qui elle prétend être. Nous transférons
          également au Client et/ou donnons aux Clients un accès leur permettant
          de télécharger les informations que nous recueillons auprès de vous, y
          compris vos Données à caractère personnel, sur instruction et à la
          demande de notre Client.
        </p>
        <p>
          Plaid utilise vos Données à caractère personnel pour fournir les
          Services IDV à nos Clients et pour aider nos Clients à se conformer à
          leurs obligations légales. Nous pouvons également utiliser des
          informations sur les appareils ou des technologies de suivi pour
          identifier et prévenir la fraude dans les Services IDV. Par exemple,
          nous traitons vos Données à caractère personnel par l'intermédiaire de
          notre service de vérification d'identité basé sur le cloud pour
          renvoyer des informations de correspondance à nos Clients pour des
          événements ou activités particuliers sur le Site client. En outre,
          lorsque nos Clients utilisent l'authentification à deux facteurs, nous
          traitons vos Données à caractère personnel, telles que votre numéro de
          téléphone, pour vous envoyer un code de vérification par SMS. Cela
          aide nos Clients qui utilisent cette fonctionnalité à vérifier les
          identités en garantissant la possession des numéros de téléphone
          associés aux identités.
        </p>
      </div>

      <div id='section-fr-5'>
        <h5>
          5. Comment nous pouvons utiliser des données anonymisées ou regroupées
          pour maintenir, améliorer et développer les Services IDV
        </h5>
        <p>
          Nous pouvons anonymiser, dépersonnaliser ou regrouper des données pour
          maintenir, améliorer et optimiser les Services IDV. Nous pouvons
          également utiliser des données anonymisées, dépersonnalisées ou
          regroupées pour prévenir et détecter la fraude dans les Services IDV.
          Plaid IDV n'utilise jamais vos Données à caractère personnel pour
          maintenir, améliorer, optimiser ou développer les Services IDV.
        </p>
      </div>

      <div id='section-fr-6'>
        <h5>6. Vos droits et choix</h5>
        <p>
          En fonction de votre emplacement et sous réserve de la loi applicable,
          vous pouvez avoir les droits concernant vos Données à caractère
          personnel que nous traitons pour le compte du Client.
        </p>
        <p>
          <strong>Droits en matière de protection des données.</strong> Lorsque
          nous agissons en tant que sous-traitant des données, sous-traitant
          ultérieur ou fournisseur de services pour le compte de nos Clients,
          vous devez adresser toute demande d'accès à vos Données à caractère
          personnel ou d'exercice de vos droits en matière de protection des
          données au Client. Nous aiderons nos Clients à répondre à votre
          demande et agirons selon leurs instructions.
        </p>
        <p>
          Veuillez consulter la Politique de confidentialité de l'utilisateur
          final de Plaid à l'adresse{' '}
          <Link href='https://plaid.com/legal/#consumers'>
            <a>https://plaid.com/legal/#consumers</a>
          </Link>{' '}
          pour plus de détails sur la manière dont nous traitons les demandes
          lorsque nous sommes responsables du traitement des données à caractère
          personnel en tant que responsable du traitement.
        </p>
      </div>

      <div id='section-fr-7'>
        <h5>7. Sécurité et conservation</h5>
        <p>
          <strong>Garanties de sécurité.</strong> Nous appliquons des mesures de
          sécurité techniques et organisationnelles conçues pour protéger les
          Données à caractère personnel traitées dans le cadre des Services IDV
          contre tout accès, divulgation, altération et destruction non
          autorisés.
        </p>
        <p>
          <strong>Conservation des données.</strong> Les Clients contrôlent
          leurs politiques de conservation des données et peuvent spécifier
          combien de temps conserver vos Données à caractère personnel.
          Lorsqu'un Client vous le demande, vos Données à caractère personnel
          seront supprimées ou anonymisées.
        </p>
      </div>

      <div id='section-fr-8'>
        <h5>
          8. Mises à jour de la présente Déclaration de confidentialité IDV
        </h5>
        <p>
          Nous pouvons réviser la Déclaration de confidentialité IDV de temps à
          autre en réponse aux demandes de nos Clients concernant les Services
          IDV et aux évolutions juridiques, techniques ou commerciales. Nous
          produirons toutes les mises à jour sur notre Site et la version
          révisée entrera en vigueur lorsqu'elle sera publiée. Si nous apportons
          des modifications importantes aux manières grâce auxquelles dont nous
          utilisons ou partageons les Données à caractère personnel précédemment
          recueillies auprès de vous, nous publierons la version mise à jour
          ici. Vous pouvez voir quand la présente Déclaration de confidentialité
          IDV a été mise à jour pour la dernière fois en vérifiant la date de «
          dernière mise à jour » ou d'« entrée en vigueur » affichée en haut de
          cette page.
        </p>
      </div>

      <div id='section-fr-9'>
        <h5>9. Contacter Plaid</h5>
        <p>
          Si vous avez des questions ou des commentaires concernant la présente
          Déclaration de confidentialité IDV ou nos pratiques de
          confidentialité, veuillez contacter Plaid à l'adresse{' '}
          <Link href='mailto:privacy@plaid.com'>
            <a>privacy@plaid.com</a>
          </Link>{' '}
          ou par courrier à l'adresse:
        </p>
        <p>
          Plaid Inc.
          <br />
          À l'attention de: Legal
          <br />
          PO Box 7775 #35278
          <br />
          San Francisco, Californie 94120-7775
          <br />
          États-Unis
        </p>
      </div>

      <div id='section-fr-10'>
        <h5>
          10. Pour les personnes résidant dans l'Union européenne et au
          Royaume-Uni
        </h5>
        <p>
          Plaid a désigné Plaid B.V. en tant que Représentant en charge de la
          protection des données aux fins du RGPD dans l'UE/EEE et de la loi sur
          la protection des données de 2018 (telle que modifiée) au Royaume-Uni.
          Si vous résidez dans l'UE/EEE ou au Royaume-Uni et que nous avons
          traité ou traitons vos Données à caractère personnel, vous pouvez
          avoir le droit d'exercer vos droits en vertu du RGPD.
        </p>
        <p>
          Si notre Client au nom duquel nous avons traité vos Données à
          caractère personnel est également situé dans l'EEE ou au Royaume-Uni,
          vous devez contacter notre Client au sujet de vos Données à caractère
          personnel au lieu du Représentant en charge de la protection des
          données de Plaid. Si vous souhaitez toujours poser une question à
          Plaid, ou exercer autrement vos droits concernant vos Données à
          caractère personnel, vous pouvez le faire comme suit:
        </p>
        <ul>
          <li>en envoyant un e-mail à privacy@plaid.com; ou</li>
          <li>
            en contactant notre représentant de la protection des données via le
            formulaire en ligne à l'adresse:{' '}
            <Link href='https://plaid.com/legal/data-protection-request-form/'>
              <a>https://plaid.com/legal/data-protection-request-form/</a>
            </Link>
            .
          </li>
        </ul>
        <p>
          Vous pouvez également envoyer votre demande de renseignements à Plaid
          aux adresses suivantes:
        </p>
        <p>
          <em>Si vous résidez au Royaume-Uni:</em>
        </p>
        <p>
          Plaid Financial Ltd.
          <br />
          À l'attention de: Legal
          <br />
          Techspace Level 4, 25 Worship Street
          <br />
          Londres, Royaume-Uni, EC2A 2DX
        </p>
        <p>
          <em>Si vous résidez dans l'EEE:</em>
        </p>
        <p>
          Plaid B.V.
          <br />
          À l'attention de: Legal
          <br />
          Muiderstraat 1<br />
          1011PZ Amsterdam
          <br />
          Pays-Bas
        </p>
        <p>
          À la réception de votre correspondance, Plaid est susceptible de
          demander une preuve de votre identité pour s'assurer que vos Données à
          caractère personnel ne sont pas remises à quiconque d'autre que vous.
        </p>
      </div>

      <hr className='section-divider' />
    </>
  );
};

export default FrVersion;
